import MultiSelectComponent from "../../../../components/multi-select/multi-select.component";
import SingleSelectComponent from "../../../../components/single-select/single-select.component";
import SelectSiteGroupComponent from "../../../../components/site/select-site-group.component";
import SelectSiteComponent from "../../../../components/site/select-site.component";
import TreeInputComponent from "../../../../components/treeview/tree-input.component";
import { codeMasterType } from "../../../../config/constants";
import codeMasterModel from "../../../../models/common/code-master.model";
import siteModel from "../../../../models/site/site.model";
import ViewBase from "../../../view-base.view";

/**
 * Country Map Form Card view
 * 
 * This component is to represent a single country map card.
 * Country is required to be passed in so that it's able to retrieve all the required dropdown values.
 * User is passed in to fill in the inputs.
 * 
 * @author Darrus Goh
 */
export default function CountryMapFormCardView() {
  ViewBase.call(this);

  let userCountryMapClone = null;
  let locations = [];
  let userRoles = [];
  let BU = [];
  let retailDivisions = [];
  let gscmDivisions = [];
  let country = null;

  const getLocations = () => {
    return siteModel.getByType({ type: 'location', countryCode: country?.code })
      .then((result) => {
        if (result) {
          const regionMap = {};
          const rootLocation = result[0];
          regionMap[rootLocation.code] = { code: rootLocation.code, name: rootLocation.name, children: [] };

          for (let i = 1; i < result.length; ++i) {
            const location = result[i];
            const node = { code: location.code, name: location.name, parentCode: location.parentCode, children: [] };
            regionMap[location.code] = node;

            if (regionMap.hasOwnProperty(location.parentCode)) {
              regionMap[location.parentCode].children.push(node);
            }
          }

          locations = [regionMap[rootLocation.code]];
        }
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  const getUserRoles = () => {
    codeMasterModel.getByCategoryV2({ category: codeMasterType.userRole, countryCode: country?.code })
      .then((result) => {
        if (result) {
          userRoles = result;
        }
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  const getBU = () => {
    codeMasterModel.getByCategoryV2({ category: codeMasterType.bu, countryCode: country?.code })
      .then((result) => {
        if (result) {
          BU = result;
        }
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  const getRetailDivisions = () => {
    codeMasterModel.getByCategoryV2({ category: codeMasterType.div, countryCode: country?.code })
      .then((result) => {
        if (result) {
          retailDivisions = result;
        }
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  const getGSCMDivisions = () => {
    codeMasterModel.getByCategoryV2({ category: codeMasterType.gscmDiv, countryCode: country?.code })
      .then((result) => {
        if (result) {
          gscmDivisions = result;
        }
      })
      .catch((error) => {
        this.handleError(error);
      });
  }

  return {
    oninit: ({ attrs: { country: _country } }) => {
      country = _country;
      if (country) {
        getLocations();
        getUserRoles();
        getBU();
        getRetailDivisions();
        getGSCMDivisions();
      }
    },
    view: ({ attrs: { isDefaultCountry, userCountryMap, onChange, } }) => {
      userCountryMapClone = structuredClone(userCountryMap);

      return [
        m(`#countryMapCard-${country?.code}.card`,
          m(".card-body.py-4",
            m('.row.mb-4',
              m(".col-12",
                m(".hstack.justify-content-between.align-items-middle",
                  m('h2.mb-0', country?.name),
                  !isDefaultCountry && m("button.btn.btn-danger.btn-remove", { type: 'button', onclick: () => onChange(null) }, this.getText("LT00000237", "Remove"))
                )
              ),
            ),
            m('.row.g-3',
              m('.col-md-6.position-relative',
                m('label.form-label.mandatory', this.getText("LT00000060", "User Role")),
                m(SingleSelectComponent, {
                  items: userRoles,
                  selectedItem: userCountryMap?.userRole,
                  keyOnly: true,
                  onSelect: (v) => {
                    userCountryMapClone.userRole = v;
                    onChange(userCountryMapClone);
                  },
                  required: true,
                  filterable: true
                }),
                m('.invalid-tooltip', this.getText("LT00000729", "This field is required"))
              ),
              m('.col-md-6',
                m('.hstack.h-100.gap-4',
                  m('div.h-100',
                    m('.vstack.h-100',
                      m('.form-label-empty'),
                      m('.flex-grow-1',
                        m('.d-flex.h-100.align-items-center',
                          m('.form-check',
                            m('input.form-check-input', {
                              id: `adminCheckbox${country?.code}`,
                              type: 'checkbox',
                              checked: userCountryMap?.admin,
                              onclick: (e) => {
                                userCountryMapClone.admin = e.target.checked;
                                onChange(userCountryMapClone);
                              },
                            }),
                            m('label.form-check-label', { for: `adminCheckbox${country?.code}` }, this.getText("LT00001334", "Subsidiary System Admin"))
                          )
                        )
                      )
                    )
                  ),
                  m('div.h-100',
                    m('.vstack.h-100',
                      m('.form-label-empty'),
                      m('.flex-grow-1',
                        m('.d-flex.h-100.align-items-center',
                          m('.form-check',
                            m('input.form-check-input', {
                              id: `requireApprovalCheckbox${country?.code}`,
                              type: 'checkbox',
                              checked: userCountryMap?.requiredApproval,
                              onclick: (e) => {
                                userCountryMapClone.requiredApproval = e.target.checked;
                                onChange(userCountryMapClone);
                              },
                            }),
                            m('label.form-check-label', { for: `requireApprovalCheckbox${country?.code}` }, this.getText("EM00000292", "Visit Plan required approval"))
                          )
                        )
                      )
                    )
                  )
                )
              ),
              m('.col-md-4',
                m('label.form-label', this.getText("LT00000239", "BU")),
                m(MultiSelectComponent, {
                  items: BU,
                  selectedItems: userCountryMap?.BU,
                  keyOnly: true,
                  filterable: false,
                  onSelect: (v) => {
                    userCountryMapClone.BU = v;
                    onChange(userCountryMapClone);
                  }
                })
              ),
              m('.col-md-4',
                m('label.form-label', this.getText("LT00000240", "Retail Division")),
                m(MultiSelectComponent, {
                  items: retailDivisions,
                  selectedItems: userCountryMap?.retailDivisions,
                  keyOnly: true,
                  filterable: false,
                  onSelect: (v) => {
                    userCountryMapClone.retailDivisions = v;
                    onChange(userCountryMapClone);
                  }
                })
              ),
              m('.col-md-4',
                m('label.form-label', this.getText("LT00000241", "GSCM Division")),
                m(MultiSelectComponent, {
                  items: gscmDivisions,
                  selectedItems: userCountryMap?.gscmDivisions,
                  keyOnly: true,
                  onSelect: (v) => {
                    userCountryMapClone.gscmDivisions = v;
                    onChange(userCountryMapClone);
                  }
                })
              ),
              m('.col-md-6',
                m('label.form-label', this.getText("LT00000229", "Managed Site")),
                m(SelectSiteComponent, {
                  countryCode: country?.code,
                  selectedItemList: userCountryMap?.managedSites,
                  onChange: (s) => {
                    userCountryMapClone.managedSites = s;
                    onChange(userCountryMapClone);
                  }
                })
              ),
              m('.col-md-6'),
              m('.col-md-6',
                m('label.form-label', this.getText("LT00000230", "Managed Site Group")),
                m(SelectSiteGroupComponent, {
                  selectedItems: userCountryMap?.managedSiteGroups,
                  countryCode: country?.code,
                  onChange: (g) => {
                    userCountryMapClone.managedSiteGroups = g;
                    onChange(userCountryMapClone);
                  }
                })
              ),
              m('.col-md-6'),
              m('.col-md-6',
                m('label.form-label', this.getText("LT00000231", "Managed location")),
                m(TreeInputComponent, {
                  items: locations,
                  selectedItems: userCountryMap?.managedLocations,
                  title: this.getText("LT00000231", "Managed location"),
                  isLevel1Expanded: true,
                  onChange: (selectedParentCodes) => {
                    userCountryMapClone.managedLocations = selectedParentCodes;
                    onChange(userCountryMapClone);
                  }
                })
              )
            )
          )
        ),
        this.getBaseViews()
      ]
    }
  };
}
